import { useEffect } from "react";
import Countdown from "react-countdown";
import { useNavigate } from "react-router-dom";

import CheckIcon from "@mui/icons-material/Check";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid,
  Typography,
} from "@mui/material";
import ProChip from "@/components/ProChip";

import { SHOW_COUNT_DOWN_LIMIT } from "@/constants/config";
import { MAX_FLIGHT_RANGE } from "@/constants/membership";
import { PRO_ALERT_LIMIT } from "@/constants/membership";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import usePlatform from "@/hooks/usePlatform";
import useThemeData from "@/hooks/useThemeData";
import useUserInfo from "@/hooks/useUserInfo";

const lightBg = "https://img.awardtool-api.com/home_flights_light_theme_v2.jpg";
const darkBg = "https://img.awardtool-api.com/home_flights_dark_theme_v2.jpg";

export const RenderFeaturePC = () => (
  <div className="w-full max-w-[1180px] m-auto px-4 sm:px-6 lg:px-8 pb-[10px]">
    <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
      <Typography className={`flex items-center`} sx={{ fontWeight: "bold" }}>
        <CheckIcon color="primary" className="mr-[2px]" />
        <span className="text-left ml-[3px]">365-day Panoramic View</span>
      </Typography>
      <Typography className={`flex items-center`} sx={{ fontWeight: "bold" }}>
        <CheckIcon color="primary" className="mr-[2px]" />
        <span className="text-left ml-[3px]">{MAX_FLIGHT_RANGE + "-entry Real-time Search"}</span>
      </Typography>
      <Typography className={`flex items-center`} sx={{ fontWeight: "bold" }}>
        <CheckIcon color="primary" className="mr-[2px]" />
        <span className="text-left ml-[3px]">{PRO_ALERT_LIMIT} Advanced Alerts with SMS Notifications</span>
      </Typography>
      <Typography className={`flex items-center`} sx={{ fontWeight: "bold" }}>
        <CheckIcon color="primary" className="mr-[2px]" />
        <span className="text-left ml-[3px]">Millions of Flight and Hotel Deals</span> 
      </Typography>
      <Typography className={`flex items-center`} sx={{ fontWeight: "bold" }}>
        <CheckIcon color="primary" className="mr-[2px]" />
        <span className="text-left ml-[3px]">Unlimited Panorama Alerts & Seat Map</span>
      </Typography>
      <Typography className={`flex items-center`} sx={{ fontWeight: "bold" }}>
        <CheckIcon color="primary" className="mr-[2px]" />
        <span className="text-left ml-[3px]">Premium Ad-Free Experience</span>
      </Typography>
    </div>
  </div>
);

export const RenderFeatureMobile = () => (
  <Grid container spacing={1} sx={{ textAlign: "center", width: "295px", margin: "0 auto" }}>
    <Grid item xs={12}>
      <Typography className="flex items-center" sx={{ fontWeight: "bold" }}>
        <CheckIcon color="primary" className="mr-[5px]" />
        {"" + MAX_FLIGHT_RANGE + "-entry Real-time Search"}
      </Typography>
    </Grid>
    <Grid item xs={12}>
      <Typography className="flex items-center" sx={{ fontWeight: "bold" }}>
        <CheckIcon color="primary" className="mr-[5px]" />
        365-day Panoramic View
      </Typography>
    </Grid>
    <Grid item xs={12}>
      <Typography className="flex items-center" sx={{ fontWeight: "bold" }}>
        <CheckIcon color="primary" className="mr-[5px]" />
        {PRO_ALERT_LIMIT} AwardTool Advanced Alerts
      </Typography>
    </Grid>
    <Grid item xs={12}>
      <Typography className="flex items-center" sx={{ fontWeight: "bold" }}>
        <CheckIcon color="primary" className="mr-[5px]" />
        Millions of Flight and Hotel Deals
      </Typography>
    </Grid>
  </Grid>
);

export const RenderPromotion = (props: {
  popup: boolean;
  has_max_width?: boolean;
  borderThickness?: number;
}) => {
  const { popup = false, has_max_width = true, borderThickness = 1 } = props;
  const store = useGlobalStoreContext();
  const theme = useThemeData();
  let beforeCode, afterCode, title, couponCode, expireSec;
  if (store.promotion) {
    const text = store.promotion.text;
    couponCode = store.promotion.code;
    title = store.promotion.title;
    expireSec = (store.promotion.expire - Math.floor(Date.now() / 1000)) * 1000;
    // Split the text into two parts: before the code and after the code
    const index = text.indexOf(couponCode);
    if (index !== -1) {
      beforeCode = text.substring(0, index);
      afterCode = text.substring(index + couponCode.length);
    } else {
      beforeCode = text;
      afterCode = "";
    }
  }
  return store.promotion && expireSec && expireSec > 0 ? (
    <div className={popup ? "text-center mt-[20px]" : "text-center mt-[8px]"}>
      <Typography
        sx={{
          py: 0.6,
          fontWeight: "bold",
          maxWidth: has_max_width ? "900px" : "100%",
          borderRadius: "10px",
          margin: "0 auto",
          border: `${borderThickness}px solid`,
          borderColor: theme.palette.primary.main,
          paddingX: "5px",
          paddingY: "10px",
          background: theme.isDarkMode ? "black" : "white",
        }}
      >
        🎉 {title} {beforeCode}{" "}
        <Button sx={{ fontWeight: 800 }} variant="contained" size="small">
          {couponCode}
        </Button>{" "}
        {afterCode} {"   "}{" "}
        {expireSec < SHOW_COUNT_DOWN_LIMIT ? (
          <>
            Ends in <Countdown date={Date.now() + expireSec} /> 🔔
          </>
        ) : null}
      </Typography>
    </div>
  ) : null;
};
const ProInfoBanner = () => {
  const { isPc } = usePlatform();

  const navigate = useNavigate();

  const theme = useThemeData();

  const store = useGlobalStoreContext();

  let beforeCode, afterCode, title, couponCode, expireSec;
  if (store.promotion) {
    const text = store.promotion.text;
    couponCode = store.promotion.code;
    title = store.promotion.title;
    expireSec = (store.promotion.expire - Math.floor(Date.now() / 1000)) * 1000;
    // Split the text into two parts: before the code and after the code
    const index = text.indexOf(couponCode);
    if (index !== -1) {
      beforeCode = text.substring(0, index);
      afterCode = text.substring(index + couponCode.length);
    } else {
      beforeCode = text;
      afterCode = "";
    }
  }

  return (
    <div className="w-full">
      <Typography className="text-center !mb-[5px]" sx={{ fontWeight: "bold" }}>
        Save thousands of dollars and hours of time with <span> <ProChip height="26px" useDiamond={true}/> </span> <span className="ml-1">today!</span>
      </Typography>

      {isPc ? <RenderFeaturePC /> : <RenderFeatureMobile />}

      <RenderPromotion popup={false} />
      <div className="flex justify-center">
        <Button
          variant="contained"
          size="large"
          sx={{
            fontWeight: 800,
            marginTop: "10px",
            fontSize: "17px",
            marginBottom: "4px",
            borderRadius: "18px"
          }}
        >
          Upgrade Now!
        </Button>
      </div>
    </div>
  );
};

const ProInfoUseInRoute = (props: { measure?: () => void }) => {
  const { isPc } = usePlatform();

  const { userInfoContext } = useUserInfo();
  const store = useGlobalStoreContext();

  const { measure } = props;

  const theme = useThemeData();
  const navigate = useNavigate();

  useEffect(() => {
    if (measure) {
      measure();
    }
  }, [measure, userInfoContext.userInfo?.token, store.promotion]);

  if (!userInfoContext.userInfo?.token) {
    return null;
  }

  return (
    <div
      onClick={() => {
        window.open("/pro", "_blank", "noopener,noreferrer");
      }}
      style={{
        paddingTop: "15px",
        paddingBottom: "15px",
        ...(isPc
          ? {
            backgroundColor: theme.isDarkMode ? "black" : "#FAF9F6",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "top",
            backgroundSize: "cover",
          }
          : {}),
      }}
    >
      <ProInfoBanner />
    </div>
  );
};

const ProInfoUseInFlight = (props: { measure?: () => void }) => {
  const { isPc } = usePlatform();

  const { userInfoContext } = useUserInfo();

  const { measure } = props;

  const theme = useThemeData();
  const navigate = useNavigate();

  useEffect(() => {
    if (measure) {
      measure();
    }
  }, [measure, userInfoContext.userInfo?.token]);

  if (!userInfoContext.userInfo?.token) {
    return null;
  }

  return (
    <Accordion
      expanded={false}
      onChange={() => { }}
      onClick={() => {
        window.open("/pro", "_blank", "noopener,noreferrer");
      }}
      TransitionProps={{ unmountOnExit: true }}
      sx={{
        boxShadow: "2",
        borderRadius: "0",
        border: (theme) => `0px solid ${theme.palette.divider}`,
        textAlign: "center",
        width: "100%",
        ...(isPc
          ? {
            backgroundColor: theme.isDarkMode ? "black" : "#FAF9F6",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "top",
            backgroundSize: "cover",
          }
          : {}),
      }}
    >
      <AccordionSummary>
        <ProInfoBanner />
      </AccordionSummary>

      <AccordionDetails
        className="!pt-[15px] !pb-[20px]"
        sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}` }}
      />
    </Accordion>
  );
};

export { ProInfoUseInRoute, ProInfoUseInFlight };
